// ============================================================================
// CONFIG SETTINGS HOOK
// ============================================================================
// Central state management for the Configuration Settings screen

import { useState, useCallback, useEffect, useRef } from 'react';
import { configService } from 'api/services/configService';
import { notificationService } from 'services/notificationService';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';

export type ScopeType = 'SYSTEM' | 'PROPERTY' | 'RATE_PLAN';

/**
 * Deep-link handoff. A screen that wants Configuration opened on a specific
 * group writes the group key here, then navigates to the `config-settings`
 * module (see ApprovalInboxScreen's "Approval routing" link). sessionStorage
 * rather than a prop because main_layout constructs screens with a fixed prop
 * set, and rather than localStorage because the intent is good for exactly one
 * navigation — it is consumed and cleared on the next group load.
 */
export const CONFIG_PENDING_GROUP_KEY = 'crs.config.pendingGroupKey';

/** Requests that Configuration open on `groupKey` the next time it mounts. */
export const requestConfigGroup = (groupKey: string): void => {
  try { sessionStorage.setItem(CONFIG_PENDING_GROUP_KEY, groupKey); } catch { /* non-fatal */ }
};

export interface ConfigSettingsState {
  groups: ConfigGroup[];
  groupsLoading: boolean;
  activeGroupKey: string | null;
  scopeType: ScopeType;
  selectedPropertyId: string | null;
  selectedRatePlanId: string | null;
  effectiveValues: ConfigEffectiveValue[];
  definitions: ConfigDefinition[];
  editedValues: Record<string, ConfigValueUpdate>;
  hasChanges: boolean;
  loading: boolean;
  saving: boolean;
  error: Error | null;
}

/**
 * @param areas Setup areas whose groups this screen shows (config_group.setup_area; null = no
 * area). Omitted = every group (settings cleanup C7).
 */
export const useConfigSettings = (areas?: ReadonlyArray<string | null>) => {
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeType>('SYSTEM');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null
  );
  const [selectedRatePlanId, setSelectedRatePlanId] = useState<string | null>(
    null
  );
  const [effectiveValues, setEffectiveValues] = useState<
    ConfigEffectiveValue[]
  >([]);
  const [definitions, setDefinitions] = useState<ConfigDefinition[]>([]);
  const [editedValues, setEditedValues] = useState<
    Record<string, ConfigValueUpdate>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hasChanges = Object.keys(editedValues).length > 0;

  // Track whether first group was auto-selected
  const autoSelectedRef = useRef(false);

  // Fetch all groups on mount
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const response = await configService.getGroups();
      const sorted = [...response.data]
        .filter((g) => !areas || areas.includes(g.setupArea ?? null))
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setGroups(sorted);
      // Auto-select the requested group if a deep link asked for one and it
      // actually exists, else the first group. The request is consumed either
      // way so a later plain visit lands on the default.
      if (sorted.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        let requested: string | null = null;
        try {
          requested = sessionStorage.getItem(CONFIG_PENDING_GROUP_KEY);
          if (requested) sessionStorage.removeItem(CONFIG_PENDING_GROUP_KEY);
        } catch { /* non-fatal */ }
        const target = requested && sorted.some((g) => g.groupKey === requested)
          ? requested
          : sorted[0].groupKey;
        setActiveGroupKey(target);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setGroupsLoading(false);
    }
    // `areas` is a module-level constant in every caller, so this stays stable.
  }, [areas]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Build scopes array from current scope type + selections
  const buildScopes = useCallback(
    (
      scope: ScopeType,
      propertyId: string | null,
      ratePlanId: string | null
    ): Array<{ type: string; id: string }> => {
      const scopes: Array<{ type: string; id: string }> = [];
      if ((scope === 'PROPERTY' || scope === 'RATE_PLAN') && propertyId) {
        scopes.push({ type: 'PROPERTY', id: propertyId });
      }
      if (scope === 'RATE_PLAN' && ratePlanId) {
        scopes.push({ type: 'RATE_PLAN', id: ratePlanId });
      }
      return scopes;
    },
    []
  );

  // Fetch definitions + effective values when group or scope changes
  const fetchGroupData = useCallback(
    async (
      groupKey: string,
      scope: ScopeType,
      propertyId: string | null,
      ratePlanId: string | null
    ) => {
      setLoading(true);
      setError(null);
      try {
        const scopes = buildScopes(scope, propertyId, ratePlanId);

        const [defsResponse, effectiveResponse] = await Promise.all([
          configService.getDefinitions(groupKey),
          configService.getEffectiveConfig(groupKey, scopes),
        ]);

        setDefinitions(defsResponse.data);
        setEffectiveValues(effectiveResponse.data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [buildScopes]
  );

  // Re-fetch when group, scope, property, or rate plan changes
  useEffect(() => {
    if (!activeGroupKey) return;
    // For PROPERTY scope, only fetch when a property is selected
    if (scopeType === 'PROPERTY' && !selectedPropertyId) {
      setEffectiveValues([]);
      setDefinitions([]);
      return;
    }
    // For RATE_PLAN scope, need both property and rate plan
    if (scopeType === 'RATE_PLAN' && (!selectedPropertyId || !selectedRatePlanId)) {
      if (!selectedPropertyId) {
        setEffectiveValues([]);
        setDefinitions([]);
      }
      return;
    }
    setEditedValues({});
    fetchGroupData(activeGroupKey, scopeType, selectedPropertyId, selectedRatePlanId);
  }, [activeGroupKey, scopeType, selectedPropertyId, selectedRatePlanId, fetchGroupData]);

  // Auto-downgrade scope when switching to a group that doesn't support RATE_PLAN
  useEffect(() => {
    if (scopeType === 'RATE_PLAN' && definitions.length > 0) {
      const hasRatePlanLevel = definitions.some((d) => d.maxScopeLevel >= 2);
      if (!hasRatePlanLevel) {
        setScopeType('PROPERTY');
        setSelectedRatePlanId(null);
      }
    }
  }, [definitions, scopeType]);

  // Update a single value
  const updateValue = useCallback(
    (configKey: string, value?: string, listValues?: ConfigListItem[]) => {
      setEditedValues((prev) => ({
        ...prev,
        [configKey]: { configKey, value, listValues },
      }));
    },
    []
  );

  // Reset a value (for non-SYSTEM scope — signals override deletion)
  const resetValue = useCallback((configKey: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [configKey]: { configKey, value: undefined, listValues: undefined },
    }));
  }, []);

  // Discard all changes
  const discardChanges = useCallback(() => {
    setEditedValues({});
  }, []);

  // Determine the scopeId for save — the most specific scope selected
  const getScopeId = useCallback(() => {
    if (scopeType === 'RATE_PLAN' && selectedRatePlanId) return selectedRatePlanId;
    if ((scopeType === 'PROPERTY' || scopeType === 'RATE_PLAN') && selectedPropertyId)
      return selectedPropertyId;
    return undefined;
  }, [scopeType, selectedPropertyId, selectedRatePlanId]);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (!activeGroupKey || !hasChanges) return;

    setSaving(true);
    try {
      const values = Object.values(editedValues);
      await configService.updateValues({
        scopeType,
        scopeId: getScopeId(),
        values,
      });

      // Re-fetch effective values after save
      await fetchGroupData(activeGroupKey, scopeType, selectedPropertyId, selectedRatePlanId);
      setEditedValues({});
      notificationService.success(
        'Settings Saved',
        'Configuration settings have been updated successfully.'
      );
    } catch (err) {
      const error = err as Error;
      setError(error);
      notificationService.error(
        'Save Failed',
        error.message || 'Failed to save configuration settings.'
      );
    } finally {
      setSaving(false);
    }
  }, [
    activeGroupKey,
    hasChanges,
    editedValues,
    scopeType,
    selectedPropertyId,
    selectedRatePlanId,
    getScopeId,
    fetchGroupData,
  ]);

  // Select a group
  const selectGroup = useCallback((groupKey: string) => {
    setActiveGroupKey(groupKey);
  }, []);

  // Change scope type
  const changeScopeType = useCallback((newScope: ScopeType) => {
    setScopeType(newScope);
    if (newScope === 'SYSTEM') {
      setSelectedPropertyId(null);
      setSelectedRatePlanId(null);
    }
    if (newScope === 'PROPERTY') {
      setSelectedRatePlanId(null);
    }
  }, []);

  // Select a property
  const selectProperty = useCallback((propertyId: string | null) => {
    setSelectedPropertyId(propertyId);
    // Clear rate plan when property changes
    setSelectedRatePlanId(null);
  }, []);

  // Select a rate plan
  const selectRatePlan = useCallback((ratePlanId: string | null) => {
    setSelectedRatePlanId(ratePlanId);
  }, []);

  return {
    // State
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedPropertyId,
    selectedRatePlanId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    error,
    // Actions
    selectGroup,
    changeScopeType,
    selectProperty,
    selectRatePlan,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  };
};
